import { useEffect, useState } from "react";
import Page from "./Page";
import CreatePassengerModal from "../components/shared/modals/CreatePassengerModal";
import { useDispatch, useSelector } from "react-redux";
import { destinationActions } from "../store/slices/destinationSlice";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import ToggleReservationButton from "../components/pages/scheduling/ToggleReservationButton";
import SchedulingTabs from "../components/pages/scheduling/SchedulingTabs";
import { routeActions } from "../store/slices/routeSlice";
import dayjs from "dayjs";
import { DESTINATION_TYPE_ENUM } from "../constants/enums";
import CreateGroupButton from "../components/pages/scheduling/CreateGroupButton";
import { routeGroupActions } from "../store/slices/routeGroupSlice";
import SaveReservationGroupButton from "../components/pages/scheduling/SaveReservationGroupButton";
import ConfirmationModal from "../components/shared/modals/ConfirmationModal";
import ValidatedTextField from "../components/shared/inputs/ValidatedTextField";
import { textValidator } from "../helpers/validators/input-validators";
import { MuiColorInput } from "mui-color-input";
import { enqueueSnackbar } from "notistack";

const Scheduling = () => {
  const [search, setSearch] = useState("");
  const [detailSerach, setDetailSearch] = useState("");
  const [date, setDate] = useState(dayjs().tz("Europe/Belgrade").add(1, "day"));
  const [value, setValue] = useState(0);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { activeTab, isOpenedRouteModal } = useSelector((state) => state.route);
  const { createGroupEditor, createGroupModal, selectedRoutesIds } =
    useSelector((state) => state.routeGroup);
  const { destinations, destinationsByDate } = useSelector(
    (state) => state.destination
  );
  const [groupTitle, setGroupTitle] = useState("");
  const [groupColor, setGroupColor] = useState("#000000");

  useEffect(() => {
    dispatch(destinationActions.destinationsFetch());
  }, [dispatch]);

  useEffect(() => {
    dispatch(destinationActions.destinationsByDateFetch(date));
  }, [dispatch, date]);

  const resetFiltersHandler = () => {
    const destTypeId = destinations.filter(
      (x) => x.destinationType === DESTINATION_TYPE_ENUM.ArrivalLocation
    )[activeTab].id;
    setSearch("");
    setDetailSearch("");
    dispatch(routeActions.resetFilter());
    dispatch(
      routeActions.getRoutesByDestinationTypeFetch({
        date: date,
        search: "",
        destinationTypeId: destTypeId,
      })
    );
  };

  const getRoutesByDestinationType = () => {
    if (destinationsByDate && destinationsByDate.length > 0) {
      const destTypeId = destinationsByDate.filter(
        (x) => x.destinationType === DESTINATION_TYPE_ENUM.ArrivalLocation
      )[value].id;

      dispatch(
        routeActions.getRoutesByDestinationTypeFetch({
          date: date,
          search: "",
          destinationTypeId: destTypeId,
        })
      );
    }
  };

  const saveReservationGroupHandler = () => {
    if (!selectedRoutesIds || selectedRoutesIds.length === 0) {
      dispatch(routeGroupActions.toggleCloseGroupEditor());
      enqueueSnackbar("Moraš izabrati bar jednu rezervaciju!", {
        variant: "error",
      });
      return;
    }

    dispatch(routeGroupActions.toggleOpenCreateGroupModal());
  };

  const onSucceededCreatedRouteGroup = () => {
    const destTypeId = destinations.filter(
      (x) => x.destinationType === DESTINATION_TYPE_ENUM.ArrivalLocation
    )[activeTab].id;
    setGroupTitle("");

    dispatch(routeGroupActions.toggleCloseCreateGroupModal());
    dispatch(
      routeActions.getRoutesByDestinationTypeFetch({
        date: date,
        search: "",
        destinationTypeId: destTypeId,
      })
    );
    dispatch(routeGroupActions.getRouteGroupsFetch({ date }));
  };

  const createRouteGroupHandler = () => {
    const obj = {
      title: groupTitle,
      xAxis: 0,
      yAxis: 0,
      date,
      color: groupColor,
      routes: selectedRoutesIds.map((selectedRoute) => ({ id: selectedRoute })),
    };

    dispatch(
      routeGroupActions.createRouteGroupPost({
        obj,
        onSucceededCreatedRouteGroup,
      })
    );
  };

  return (
    <Page>
      <Box
        sx={{
          width: '100%',
          p: isMobile ? 0 : 0,
        }}
      >
        {/* Reset Filters */}
        {(search !== "" || detailSerach !== "") && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: isMobile ? 2 : 3,
              px: isMobile ? 2 : 0,
            }}
          >
            <Typography
              onClick={resetFiltersHandler}
              sx={{
                fontStyle: "italic",
                fontSize: isMobile ? "16px" : "18px",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                }
              }}
            >
              Reset filters
            </Typography>
          </Box>
        )}

        {/* Scheduling Tabs */}
        <Box sx={{ 
          mb: isMobile ? 2 : 0,
          px: isMobile ? 0 : 0,
        }}>
          <SchedulingTabs
            value={value}
            setValue={setValue}
            date={date}
            setDate={setDate}
          />
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1.5 : 2,
            mb: isMobile ? 2 : 3,
            px: isMobile ? 2 : 0,
            mt: isMobile ? 2 : 3,
          }}
        >
          {!createGroupEditor && (
            <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
              <CreateGroupButton
                handleClick={() =>
                  dispatch(routeGroupActions.toggleOpenGroupEditor())
                }
              />
            </Box>
          )}
          
          {createGroupEditor && (
            <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
              <SaveReservationGroupButton 
                handleClick={saveReservationGroupHandler} 
              />
            </Box>
          )}
          
          {!isOpenedRouteModal && (
            <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
              <ToggleReservationButton
                handleClick={() => dispatch(routeActions.toggleRouteModal())}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Modals */}
      <CreatePassengerModal
        selectedDate={date}
        setSelectedDate={setDate}
        isOpen={isOpenedRouteModal}
        getRoutesByDestinationType={getRoutesByDestinationType}
        onClose={() => dispatch(routeActions.toggleRouteModal())}
      />
      
      <ConfirmationModal
        open={createGroupModal}
        title="Kreirajte grupu"
        onAccept={createRouteGroupHandler}
        onClose={() => {
          dispatch(routeGroupActions.toggleCloseCreateGroupModal());
        }}
      >
        <Box sx={{ mb: 3 }}>
          <ValidatedTextField
            value={groupTitle}
            setValue={setGroupTitle}
            label="Naslov"
            validator={textValidator}
            type="text"
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <MuiColorInput
            value={groupColor}
            onChange={(color) => setGroupColor(color)}
          />
        </Box>
      </ConfirmationModal>
    </Page>
  );
};

export default Scheduling;